<app-navbar />

<main id="main-content">
  <section class="page-hero">
    <div class="container">
      <app-breadcrumb [trail]="[{ label: 'How it works' }]" />
      <span class="eyebrow">How it works</span>
      <h1>From a raw list to verified deceased matches in minutes</h1>
      <p>
        VerifyDeceased makes deceased consumer verification simple. Upload your records, let our
        engine match them against fresh nationwide death data, and act on confirmed results the
        same day.
      </p>
      <div class="actions-row center mt-32">
        <a class="btn btn-primary btn-lg" [routerLink]="[routes.accountRegister.url]">Create your free account</a>
        <a class="btn btn-outline btn-lg" [routerLink]="[routes.pricing.url]">See pricing</a>
      </div>
    </div>
  </section>

  <!-- Data provenance. Sophisticated buyers ask this before anything else. -->
  <section class="section" aria-labelledby="sources-heading">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Data provenance</span>
        <h2 class="section-title" id="sources-heading">Where our death data comes from</h2>
        <p class="section-lead">
          We combine authoritative government records with the sources that surface a death earliest,
          then corroborate before anything is published.
        </p>
      </div>

      <div class="grid-3">
        @for (source of dataSources; track source.title) {
          <article class="feature-card">
            <span class="icon-badge" [innerHTML]="iconFor(source.icon)"></span>
            <h3>{{ source.title }}</h3>
            <p>{{ source.description }}</p>
            <span class="tag" style="margin-top: 16px;">{{ source.cadence }}</span>
          </article>
        }
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Confirmation pipeline</span>
        <h2 class="section-title">How a record is confirmed before you see it</h2>
        <p class="section-lead">
          A false positive on a living consumer is the costliest error we can make, so a single
          unverified signal is held rather than published.
        </p>
      </div>

      <div class="steps" style="max-width: 820px; margin: 0 auto;">
        @for (step of confirmationSteps; track step.title; let i = $index) {
          <div class="step">
            <span class="step-number">{{ i + 1 }}</span>
            <div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.description }}</p>
            </div>
          </div>
        }
      </div>

      <div class="text-center mt-32">
        <a class="btn btn-outline" [routerLink]="[routes.security.url]">See how we secure your data</a>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">The process</span>
        <h2 class="section-title">Five steps to clean, verified data</h2>
        <p class="section-lead">
          A repeatable weekly cycle that keeps your records accurate and your revenue protected.
        </p>
      </div>

      <div class="steps" style="max-width: 820px; margin: 0 auto;">
        @for (step of steps; track step.title; let i = $index) {
          <div class="step">
            <span class="step-number">{{ i + 1 }}</span>
            <div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.description }}</p>
            </div>
          </div>
        }
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Inputs and outputs</span>
        <h2 class="section-title">What you send and what you get back</h2>
        <p class="section-lead">
          Send us as little as a name. Get back a complete, confirmed picture on every match.
        </p>
      </div>

      <div class="grid-2">
        <div class="card">
          <h3>What you upload</h3>
          <table class="data-table">
            <tbody>
              @for (row of inputs; track row.field) {
                <tr>
                  <td>{{ row.field }}</td>
                  <td><span class="tag">{{ row.required }}</span></td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <div class="card">
          <h3>What you receive on a match</h3>
          <table class="data-table">
            <tbody>
              @for (row of outputs; track row.field) {
                <tr>
                  <td>{{ row.field }}</td>
                  <td class="muted">{{ row.note }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Two ways to verify</span>
        <h2 class="section-title">Batch files or real time API</h2>
        <p class="section-lead">Use whichever fits your workflow. Many teams use both.</p>
      </div>

      <div class="grid-2">
        <article class="feature-card">
          <h3>Batch verification</h3>
          <p>
            Upload a file of records and download confirmed matches. Ideal for periodic portfolio
            cleaning, monthly audits and suppression list updates before a campaign.
          </p>
          <ul class="check-list" style="margin-top: 18px;">
            <li>CSV or Excel upload</li>
            <li>No file size limit</li>
            <li>Results ready in minutes</li>
          </ul>
        </article>
        <article class="feature-card">
          <h3>Real time API</h3>
          <p>
            Verify a single identity or a batch through our REST API during account opening, loan
            origination or claims processing, with a confirmed result in milliseconds.
          </p>
          <ul class="check-list" style="margin-top: 18px;">
            <li>Single or bulk requests</li>
            <li>Millisecond response times</li>
            <li>Simple JSON request and response</li>
          </ul>
          <a class="btn btn-outline" [routerLink]="[routes.developers.url]" style="margin-top: 20px;">View API docs</a>
        </article>
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Questions</span>
        <h2 class="section-title">How it works, answered</h2>
      </div>
      <div class="faq-list">
        @for (faq of faqs; track faq.question) {
          <details class="faq-item">
            <summary>{{ faq.question }}</summary>
            <p>{{ faq.answer }}</p>
          </details>
        }
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="cta-band">
        <h2>Ready to verify your first list?</h2>
        <p>
          Creating an account is free and needs no credit card. Upload a file in minutes and pay
          $0.04 only for the records we confirm as deceased.
        </p>
        <div class="actions-row center">
          <a class="btn btn-primary btn-lg" [routerLink]="[routes.accountRegister.url]">Create your free account</a>
          <a class="btn btn-outline btn-lg" [routerLink]="[routes.contact.url]">Contact sales</a>
        </div>
      </div>
    </div>
  </section>
</main>

<app-footer />
